import { Collection } from 'modules/core/lib/mobx-rest'

import PolicyAuthorization from 'modules/permissions/store/models/PolicyAuthorization'

/** Model that can be used as the target of a policy. */
export interface PolicyTarget {
  id: string
  /**
   * Fully namespaced class name of the model on the backend side.
   *
   * @example '::Employee'
   * @example '::Attendance::OvertimeRequest'
   */
  policyTargetClass: string
}

interface TargetAuthorization {
  target_id: string
  target_resource: string
}

class PolicyAuthorizations extends Collection<PolicyAuthorization> {
  get cacheKey(): string {
    return 'policyAuthorizations'
  }

  get indexes() {
    return ['policy_key', 'target_id', 'target_resource']
  }

  model() {
    return PolicyAuthorization
  }

  async fetch<T = unknown[]>(): Promise<T> {
    throw new Error('Use authorizePolicies instead.')
  }

  private targetsToParams(targets: PolicyTarget[]): TargetAuthorization[] {
    return targets.map((target) => ({
      target_id: target.id,
      target_resource: target.policyTargetClass,
    }))
  }

  async authorizePolicies(policyKey: string, targets: PolicyTarget[] | undefined = undefined) {
    const isContextless = !targets
    if (isContextless) {
      const existingAuthorization = this.findAuthorization(policyKey, null)
      if (!existingAuthorization) {
        // When passing an empty array to the backend, it will treat it as contextless and call the
        // policy without a target.
        // FIXME: We should change the backend to receive a nilable array and end this weirdness.
        await this.fetchAuthorizations(policyKey, [])
      }
      return this
    }

    if (!targets.length) {
      // HACK ALERT: When we receive an empty array we ALSO need to fetch the policy as if it was
      // contextless, this an unfortunate side effect of two things that created an implicit
      // semantic that we now need to maintain for compatibility:
      //
      // 1. The backend treats an empty array as "contextless", meaning that, when passed an empty
      //    array, it WILL call the policy, passing nil as the target.
      //
      // 2. The frontend used to require the targets array to be passed to the method, so people
      //    started passing an empty array to indicate that it was contextless, and... it worked,
      //    because the backend treated it as such (:yoro:)
      //
      // Which means that we now have lots of places where we pass an empty array to this method to
      // fetch policies that don't have a target. We need to keep this behavior for compatibility,
      // until we can update all those places to NOT pass an array and thus make it explicit that
      // it's a contextless policy, as in, it doesn't have a target.
      //
      // The good news is that we are not breaking anything... we're just making some potentially
      // unnecessary requests to the backend, which is not ideal, but it's not a huge deal either as
      // they were already done before.

      const existingContexlessAuthorization = this.findAuthorization(policyKey, null)
      if (!existingContexlessAuthorization) {
        await this.fetchAuthorizations(policyKey, [])
      }

      return this
    }

    const targetsToFetch = targets.filter((target) => !this.findAuthorization(policyKey, target))
    if (!targetsToFetch.length) {
      return this
    }

    await this.fetchAuthorizations(policyKey, targetsToFetch)

    return this
  }

  private async fetchAuthorizations(policyKey: string, targets: PolicyTarget[]) {
    const result = await this.rpc('authorize_policies', {
      policy_keys: [policyKey],
      targets: this.targetsToParams(targets),
    })

    const items = result.map((item: any) => ({
      ...item,
      id: [item.policy_key, item.target_id, item.target_resource].join('_'),
    }))
    this.set(items, {
      remove: false,
    })
  }

  private findAuthorization(
    policyKey: string,
    target: PolicyTarget | null | undefined
  ): PolicyAuthorization | null | undefined {
    return this.find({
      policy_key: policyKey,
      target_id: target ? target.id : null,
      target_resource: target ? target.policyTargetClass : null,
    })
  }

  for(policyKey: string, target?: PolicyTarget | null | undefined): boolean {
    const policyAuthorization = this.findAuthorization(policyKey, target)

    if (!policyAuthorization) {
      return false
    }

    return policyAuthorization.attributes.authorize
  }
}

const singleton: PolicyAuthorizations = new PolicyAuthorizations()

export default singleton
